import { Step as DialogStep, StepLabel } from '@mui/material';
import { IEditableLegalClient } from '../../../../Interfaces/IEditLegalClient';
import { ILegalClient } from '../../../../Interfaces/ILegalClient';
import {
  getEmptyRelationshipByType,
  getMembers,
  ILegalTransactionRelationship,
  RelationshipByType,
} from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionRelationshipMember } from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { LegalTransactionRelationshipRoleEnum } from '../../../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipRoleEnum';
import { LegalTransactionRelationshipTypeEnum } from '../../../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipTypeEnum';
import { IMemberTestator } from '../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberTestament';
import { ITestamentRelationship } from '../../../../Interfaces/ILegalTransactionRelationship/Relationships/Testament/ILegalTransactionRelationshipTestament';
import { CustomeSlide } from '../../../core/CustomeSlide';
import { useDialogContext } from '../../../core/DialogPresenter/DialogContext';

export function arrayNotEmpty<T>(array: T[] | undefined): array is T[] {
  return Array.isArray(array) && array.length > 0;
}

export function isTestamentRelationship(
  relationship: ILegalTransactionRelationship | undefined | null
): relationship is ITestamentRelationship {
  return (
    !!relationship &&
    relationship.idRelationshipType ===
      LegalTransactionRelationshipTypeEnum.Testament
  );
}

export const TransitionStatus = {
  ENTERING: 'entering',
  EXITING: 'exiting',
  IDLE: 'idle',
} as const;

export type TransitionStatus =
  (typeof TransitionStatus)[keyof typeof TransitionStatus];

type DialogStep = {
  name: string;
  condition?: () => boolean;
  content: JSX.Element;
};

type CreateStepperArrayOpts = {
  steps: { name: string }[];
  currentPage: number;
};

export const createStepperArray = ({
  steps,
  currentPage,
}: CreateStepperArrayOpts) => {
  const { goToPage } = useDialogContext();
  return steps.map((step, index) => (
    <DialogStep
      sx={{ cursor: 'pointer' }}
      key={index}
      completed={index < currentPage}
      active={index === currentPage}
      onClick={() => {
        console.log(`Clicked on step ${index + 1}: ${step.name}`);
        goToPage(index);
      }}
    >
      <StepLabel>{step.name}</StepLabel>
    </DialogStep>
  ));
};

type CreateChildrenOpts = {
  steps: DialogStep[];
  currentPage: number;
  previousPage: number;
  transitionStatus: TransitionStatus;
  onEntered?: () => void;
  onExited?: () => void;
};

export const createChildren = ({
  steps,
  currentPage,
  previousPage,
  transitionStatus,
  onEntered,
  onExited,
}: CreateChildrenOpts) => {
  return steps
    .filter(step => (step.condition ? step.condition() : true))
    .map((step, index) => (
      <CustomeSlide
        key={index}
        currentPage={currentPage}
        lastCurrentPage={previousPage}
        pageNumber={index}
        transitionStatus={transitionStatus}
        appear={false}
        onEntered={onEntered}
        onExited={onExited}
      >
        {step.content}
      </CustomeSlide>
    ));
};

export const updateOrCreateLegalClient = <T extends ILegalClient>(
  legalClientArray: T[],
  updatedLegalClient: T
): T[] => {
  const existingClient = legalClientArray.find(
    client => client.idLegalClient === updatedLegalClient.idLegalClient
  );

  if (existingClient) {
    return legalClientArray.map(client =>
      client.idLegalClient === updatedLegalClient.idLegalClient
        ? updatedLegalClient
        : client
    );
  }

  return [...legalClientArray, updatedLegalClient];
};

export const updateOrCreateRelationship = (
  relationshipArray: ILegalTransactionRelationship[],
  updatedRelationship: ILegalTransactionRelationship
): ILegalTransactionRelationship[] => {
  const existingRelationship = relationshipArray.find(
    r => r.idRelationship === updatedRelationship.idRelationship
  );

  if (existingRelationship) {
    return updateRelationship(relationshipArray, updatedRelationship);
  }

  // Create new relationship
  return [...relationshipArray, updatedRelationship];
};

export const updateRelationship = (
  relationshipArray: ILegalTransactionRelationship[],
  updatedRelationship: ILegalTransactionRelationship
): ILegalTransactionRelationship[] => {
  return relationshipArray.map(existingRelationship =>
    existingRelationship.idRelationship === updatedRelationship.idRelationship
      ? updatedRelationship
      : existingRelationship
  );
};

export const arrayIncludes = <U extends T, T>(
  array: readonly U[],
  item: T
): item is U => {
  return array.includes(item as U);
};

export const updateOrCreateMember = <
  T extends ILegalTransactionRelationshipMember,
>(
  members: T[],
  updatedMember: T
): T[] => {
  const existingMember = members.find(
    member => member.idLegalClient === updatedMember.idLegalClient
  );

  if (existingMember) {
    return members.map(member =>
      member.idLegalClient === updatedMember.idLegalClient
        ? updatedMember
        : member
    );
  }

  return [...members, updatedMember];
};

export const createMemberIfNotExists = (
  members: ILegalTransactionRelationshipMember[],
  member: ILegalTransactionRelationshipMember
): ILegalTransactionRelationshipMember[] => {
  const existingMember = members.find(
    member => member.idLegalClient === member.idLegalClient
  );

  if (existingMember) {
    return members; // Member already exists, no need to create
  }

  return [...members, member]; // Create new member
};

export const isTestatorMember = (
  member: ILegalTransactionRelationshipMember | undefined | null
): member is IMemberTestator => {
  return (
    !!member &&
    member.idRelationshipRole === LegalTransactionRelationshipRoleEnum.Testator
  );
};

export const isMainTestatorMember = (
  member: ILegalTransactionRelationshipMember | undefined | null
): member is IMemberTestator => {
  return (
    isTestatorMember(member) && member.data.testamentRole === 'mainTestator'
  );
};

export const isSpouseTestatorMember = (
  member: ILegalTransactionRelationshipMember | undefined | null
): member is IMemberTestator => {
  return isTestatorMember(member) && member.data.testamentRole === 'spouse';
};

export type ISavedRelationshipDialogData<
  T extends ILegalTransactionRelationship['data'],
> = {
  relationshipArray: ILegalTransactionRelationship[];
  legalClientArray: ILegalClient[];
  data: T;
};

const requiredLegalClientFields: (keyof IEditableLegalClient)[] = [
  'idAcademicTitle',
  'idBusinessForm',
  'idCountry',
  'idGender',
  'idLegalClientType',
  'Birthdate',
  'FirstName',
  'LastName',
  'AddressArray',
];

export const isEditableLegalClientValid = (
  legalClient: IEditableLegalClient
): boolean =>
  requiredLegalClientFields
    .map(field => legalClient[field])
    .every(
      value =>
        value !== undefined &&
        value !== null &&
        (typeof value === 'number' ? value > 0 : value !== '')
    ) &&
  legalClient.AddressArray.some(address => address.idPostcode > 0) &&
  (legalClient.data.addToLegalTransaction ? !!legalClient.Email : true);

export function getOrCreateSingletonRelationship<
  T extends LegalTransactionRelationshipTypeEnum,
>(
  relationshipArray: ILegalTransactionRelationship[],
  relationshipTypeId: T,
  qualifier: (rel: ILegalTransactionRelationship) => boolean = rel => true
): RelationshipByType<T> {
  const existingRelationship = relationshipArray.find(
    relationship =>
      relationship.idRelationshipType === relationshipTypeId &&
      qualifier(relationship)
  ) as RelationshipByType<T> | undefined;

  if (existingRelationship) {
    return existingRelationship;
  }

  return getEmptyRelationshipByType(relationshipTypeId);
}

export const getRelationshipsByLegalClient = (
  relationshipArray: ILegalTransactionRelationship[],
  idLegalClient: number
): ILegalTransactionRelationship[] | undefined => {
  return relationshipArray.filter(relationship =>
    relationship.Members.some(member => member.idLegalClient === idLegalClient)
  );
};

export function nil<T>(v: T | null | undefined): v is null | undefined {
  return v === null || v === undefined;
}

export function present<T>(v: T | null | undefined): v is T {
  return !nil(v);
}

export function removeLegalClientFromRelationships(
  relationshipArray: ILegalTransactionRelationship[],
  idLegalClientToRemove: number
): ILegalTransactionRelationship[] {
  return relationshipArray
    .map(relationship =>
      removeLegalClientFromRelationshipMembers(
        relationship,
        idLegalClientToRemove
      )
    )
    .filter(relationship => relationship.Members.length > 0);
}

export function removeLegalClientsFromRelationships(
  relationshipArray: ILegalTransactionRelationship[],
  legalClientIdsToRemove: Set<number>
): ILegalTransactionRelationship[] {
  return relationshipArray
    .map(relationship =>
      removeLegalClientsFromRelationshipMembers(
        relationship,
        legalClientIdsToRemove
      )
    )
    .filter(relationship => relationship.Members.length > 0);
}

export function removeLegalClientFromRelationshipMembers<
  T extends ILegalTransactionRelationship,
>(relationship: T, idLegalClientToRemove: number): T {
  return {
    ...relationship,
    Members: getMembers(relationship).filter(
      member => member.idLegalClient !== idLegalClientToRemove
    ),
  };
}

export function removeLegalClientsFromRelationshipMembers<
  T extends ILegalTransactionRelationship,
>(relationship: T, legalClientIdsToRemove: Set<number>): T {
  return {
    ...relationship,
    Members: getMembers(relationship).filter(
      member => !legalClientIdsToRemove.has(member.idLegalClient)
    ),
  };
}
